import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GiftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'; // Import error icon

// Import for logging
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';

// Define the base URL for your videos
const PUBLIC_VIDEO_BASE_URL = 'https://gusaguiar.com/assets/bashar/';

const importedVideoAssets = {
  'bashar-15-minutos-counting': `${PUBLIC_VIDEO_BASE_URL}Bashar - 15 Minutos & Counting - Sample.mp4`,
  'bashar-brick-walls-beliefs': `${PUBLIC_VIDEO_BASE_URL}Bashar - Brick Walls & Beliefs - Sample.mp4`,
  'bashar-going-gamma': `${PUBLIC_VIDEO_BASE_URL}Bashar - Going Gamma - Sample.mp4`,
  'bashar-nine-levels-consciousness': `${PUBLIC_VIDEO_BASE_URL}Bashar - Nine Levels of Consciousness - Sample.mp4`,
  'bashar-the-formula': `${PUBLIC_VIDEO_BASE_URL}Bashar - The Formula - Sample.mp4`,
};

// Simulate Raw Data from Database
const rawGiftVideosFromDB = [
  {
    id: 'bashar-going-gamma',
    title: 'Bashar - Going Gamma (Sample)',
    description: 'Explore the concept of "Going Gamma" and its implications for personal transformation and energetic shifts.',
    poster: '/path/to/your/going-gamma-poster.jpg',
  },
  {
    id: 'bashar-15-minutos-counting',
    title: 'Bashar - 15 Minutes & Counting (Sample)',
    description: 'A brief yet impactful segment on managing time and energy to align with your desired reality.',
    poster: '/path/to/your/15-minutos-counting-poster.jpg',
  },
  {
    id: 'bashar-brick-walls-beliefs',
    title: 'Bashar - Brick Walls & Beliefs (Sample)',
    description: 'Understand how limiting beliefs act as "brick walls" and learn techniques to dismantle them.',
    poster: '/path/to/your/brick-walls-beliefs-poster.jpg',
  },
  {
    id: 'bashar-nine-levels-consciousness',
    title: 'Bashar - Nine Levels of Consciousness (Sample)',
    description: 'A profound introduction to the various levels of consciousness and how they influence your experience.',
    poster: '/path/to/your/nine-levels-consciousness-poster.jpg',
  },
  {
    id: 'bashar-the-formula',
    title: 'Bashar - The Formula (Sample)',
    description: 'Uncover the foundational formula that Bashar presents for manifestation and conscious creation.',
    poster: '/path/to/your/the-formula-poster.jpg',
  },
];

const UserGift = () => {
  const [giftVideos, setGiftVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoErrors, setVideoErrors] = useState({}); // State to track video loading errors
  const location = useLocation();

  // Function to handle video errors
  const handleVideoError = (videoUrl) => {
    console.error(`Failed to load video: ${videoUrl}`);
    setVideoErrors(prevErrors => ({
        ...prevErrors,
        [videoUrl]: true
    }));
  };

  useEffect(() => {
    sendCodedLog({
      message: 'UserGift page mounted.',
      event: 'user_gift_page_mount',
      ...Common.getCommonLogData(location)
    });

    const fetchAndProcessGiftVideos = async () => {
      setLoading(true);
      const rawData = rawGiftVideosFromDB;
      await new Promise(resolve => setTimeout(resolve, 500));
      const processedVideos = rawData.map(video => {
        if (importedVideoAssets[video.id]) {
          return {
            ...video,
            src: importedVideoAssets[video.id],
          };
        }
        return video;
      });
      setGiftVideos(processedVideos);
      setLoading(false);
    };

    fetchAndProcessGiftVideos();

    return () => {
      sendCodedLog({
        message: 'UserGift page unmounted.',
        event: 'user_gift_page_unmount',
        ...Common.getCommonLogData(location)
      });
    };
  }, [location]);

  const handleExploreCompleteClick = () => {
    sendCodedLog({
      message: 'User clicked "Explore Complete Bashar Transmissions" button from gift page.',
      event: 'user_gift_explore_complete_click',
      ...Common.getCommonLogData(location)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex justify-center items-center text-xl font-sans">
        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Loading your free transmissions...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6 flex flex-col items-center font-sans">
      <h1
        className="text-4xl sm:text-5xl font-bold text-yellow-400 mb-8 text-center"
        style={{ fontFamily: '"Cinzel Decorative", serif' }}
      >
        Your Free Gifts: Bashar Transmissions
      </h1>

      <p className="text-lg text-gray-300 mb-10 text-center max-w-3xl">
        Enjoy these complimentary sample transmissions from Bashar. We hope they bring you valuable insights!
      </p>

      {giftVideos.length > 0 ? (
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6 max-w-4xl">
          {giftVideos.map((video) => (
            video.src ? (
              <div key={video.id} className="w-full bg-black rounded-lg shadow-xl overflow-hidden mb-4">
                {/* Conditional rendering: Show video or error message */}
                {videoErrors[video.src] ? (
                  <div className="aspect-video w-full bg-red-950/20 border-b-4 border-gray-800 flex flex-col items-center justify-center text-center p-4">
                      <ExclamationTriangleIcon className="w-10 h-10 text-red-400 mb-3" />
                      <p className="font-semibold text-red-300 mb-2">The video player could not load.</p>
                      <a
                          href={video.src}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block py-2 px-5 rounded-lg bg-red-500 text-white font-bold hover:bg-red-400 transition-all"
                      >
                          Open Video in New Tab
                      </a>
                  </div>
                ) : (
                  <video
                    controls
                    className="w-full"
                    poster={video.poster}
                    controlsList="nodownload"
                    onError={() => handleVideoError(video.src)} // Attach error handler
                  >
                    <source src={video.src} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}

                <div className="p-4 bg-gray-800">
                  <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">{video.title}</h2>
                  <p className="text-gray-400 text-sm sm:text-base">{video.description}</p>
                </div>
              </div>
            ) : (
              <div key={video.id} className="w-full bg-gray-800 rounded-lg shadow-xl overflow-hidden mb-4 p-4">
                <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2">{video.title}</h2>
                <p className="text-gray-400 text-sm sm:text-base">{video.description}</p>
                <p className="text-red-400 mt-2">Video asset not found.</p>
              </div>
            )
          ))}
        </div>
      ) : (
        <p className="text-xl text-gray-400">No free gift transmissions available at this time.</p>
      )}


      <Link
        to="/bashar/selector"
        className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-black bg-yellow-400 hover:bg-yellow-500 md:py-4 md:text-lg md:px-10 transition-colors duration-200 mt-12"
        onClick={handleExploreCompleteClick}
      >
        Explore Complete Bashar Transmissions
      </Link>
    </div>
  );
};

export default UserGift;